/**
 * Anschluss nach einem einfachen Nachschlagen.
 * Kein neuer Analysemotor: Fall = bestehende Rechtsfrage, Rechtsprechung = enger
 * OGH-Anschluss, Schreiben = bestehender Schreibweg. Nackte Paragraphenzeile
 * fragt zuerst nach, statt zu analysieren.
 */
import type { SuggestedAction } from './portalArkoChipSink.ts';
import {
  buildParagraphLookupPrompt,
  parseParagraphQuery,
  stripAttachedParagraphPrefix,
  type ParagraphRef,
} from './portalParagraphRef.ts';
import { isComposerParagraphLookup } from './portalLexJudJoin.ts';

export const PARAGRAPH_CLARIFY_SENTENCE = 'Zur genannten Norm fehlt der Auftrag.';

export const PARAGRAPH_ANSCHLUSS_LABELS = ['Fall', 'Rechtsprechung', 'Schreiben'] as const;

/** Generisches „§§ nachschlagen“ bleibt aus. Die drei Anschluss-Namen bleiben in der Zeile. */
export function anschlussChipStaysVisible(action: string, label: string): boolean {
  if (action === 'lookup_law' && label !== PARAGRAPH_ANSCHLUSS_LABELS[0]) return false;
  return true;
}

/**
 * „Was sagt der OGH dazu?“ steht in der Chip-Zeile, sobald die Antwort Treffer hat.
 * Die Join-Merkliste schluckt diesen Chip nicht. Ohne Treffer bleibt er aus.
 * „Rechtsprechung“ ist der Anschluss-Name und folgt der anderen Zeile.
 */
export function genericOghChipVisible(action: string, label: string, hasOghHits: boolean): boolean {
  if (action !== 'ogh_dazu') return true;
  if (label === PARAGRAPH_ANSCHLUSS_LABELS[1]) return true;
  return hasOghHits;
}

export type ParagraphTurnKind = 'lookup' | 'clarify' | 'pass';

const WORTLAUT_RE = /\b(wie\s+lautet|wortlaut|gesetzestext|originalwortlaut|zitiere)\b/i;
const ERKLAERUNG_RE = /\b(erkl[aä]r\w*|erklaer\w*|was\s+bedeutet|überblick|ueberblick)\b/i;
const SCHREIBEN_RE = /\b(schreib\w*|verfass\w*|diktat|brief)\b/i;

const FLUFF = new Set([
  'was', 'ist', 'sind', 'war', 'wie', 'wo', 'wann', 'warum', 'wieso', 'weshalb',
  'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einer', 'einem', 'einen',
  'bitte', 'mal', 'dazu', 'hier', 'und', 'oder', 'von', 'zu', 'zum', 'zur', 'im', 'in',
  'fuer', 'für', 'nach', 'bei', 'mit', 'ueber', 'über', 'paragraph', 'paragraf',
  'paragraphen', 'gesetz', 'norm', 'frage', 'welchen', 'welche', 'welcher', 'welches',
  'gilt', 'genau', 'noch', 'nur', 'auch', 'mir', 'uns', 'man', 'eigentlich', 'denn',
  'doch', 'schon', 'eben', 'halt', 'nun', 'so', 'diese', 'dieser', 'dieses', 'auf',
  'an', 'am', 'vom', 'beim', 'ohne', 'unter', 'zwischen', 'sowie', 'bzw', 'thema',
  'nochmal', 'einmal', 'gibt', 'es', 'sich', 'nicht', 'kein', 'keine', 'ok', 'okay',
  'danke', 'hallo',
]);

function folded(text: string): string {
  return stripAttachedParagraphPrefix(String(text || '')).replace(/\s+/g, ' ').trim();
}

function refsOf(text: string): ParagraphRef[] {
  return parseParagraphQuery(folded(text)).filter((ref) => ref.section !== '…');
}

function hasSachverhalt(text: string, refs: ParagraphRef[]): boolean {
  let rest = folded(text);
  if (rest.length > 280) return true;
  for (const ref of refs) {
    const label = ref.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    rest = rest.replace(new RegExp(label, 'gi'), ' ');
  }
  rest = rest
    .replace(/§+/g, ' ')
    .replace(/\b(abgb|mrg|lfg|weg|kschg|zpo|eo|stgb|rao|ugb|dsg|dsgvo|wgg|jn)\b/gi, ' ')
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ');
  const left = rest
    .toLowerCase()
    .split(/\s+/)
    .filter((token) => token && !FLUFF.has(token) && !/^\d+[a-z]?$/.test(token));
  return left.length > 0;
}

/** lookup = bestehender Nachweis. clarify = Rückfrage, kein Webhook. pass = bestehender Weg. */
export function classifyParagraphTurn(text: string): ParagraphTurnKind {
  const raw = folded(text);
  if (!raw) return 'pass';
  if (isComposerParagraphLookup(raw)) return 'lookup';
  const refs = refsOf(raw);
  if (!refs.length) return 'pass';
  if (WORTLAUT_RE.test(raw)) return 'lookup';
  if (ERKLAERUNG_RE.test(raw) || SCHREIBEN_RE.test(raw)) return 'pass';
  if (hasSachverhalt(raw, refs)) return 'pass';
  return 'clarify';
}

/** Nackte oder unklare Paragraphenzeile startet die Analyse nicht. */
export function paragraphClarifyBlocksAnalysis(text: string): boolean {
  return classifyParagraphTurn(text) === 'clarify';
}

/**
 * Körper des bestehenden Nachschlage-Satzes.
 * Leer, wenn die Zeile kein Wortlaut-Nachschlagen ist.
 */
export function paragraphLookupBody(text: string): string {
  if (classifyParagraphTurn(text) !== 'lookup') return '';
  const raw = folded(text);
  if (isComposerParagraphLookup(raw)) {
    const idx = raw.search(/Bitte nur nachschlagen:/i);
    return idx >= 0 ? raw.slice(idx).trim() : raw;
  }
  const refs = refsOf(raw);
  if (!refs.length) return '';
  return buildParagraphLookupPrompt(refs);
}

export function paragraphAnschlussNormLabel(text: string): string {
  return refsOf(text).map((ref) => ref.label).join(', ');
}

/** Genau drei Wege. Keine weiteren Chips. */
export function paragraphAnschlussChips(normLabel: string): SuggestedAction[] {
  const label = String(normLabel || '').replace(/\s+/g, ' ').trim();
  const fall = label || 'die genannte Norm';
  const schreiben = label ? `Schreiben zu ${label}.` : 'Schreiben.';
  return [
    { action: 'lookup_law', label: 'Fall', followupQuery: fall },
    { action: 'ogh_dazu', label: 'Rechtsprechung' },
    { action: 'schreiben_erstellen', label: 'Schreiben', followupQuery: schreiben },
  ];
}

export function lookupShowsAnschlussChips(opts: {
  chipAction?: string;
  replyText: string;
  hasWording: boolean;
}): boolean {
  if (opts.chipAction !== 'paragraph_lookup') return false;
  if (opts.hasWording) return true;
  const reply = String(opts.replyText || '').replace(/\s+/g, ' ').trim();
  if (reply.length < 40) return false;
  if (/keine antwort erhalten|nicht erreichbar|abgebrochen|zeitüberschreitung|zeitueberschreitung/i.test(reply)) {
    return false;
  }
  return true;
}

/** Nach dem Nachweis nur die drei Chips, sonst die bisherigen Aktionen. */
export function suggestedActionsAfterLookup(
  chipAction: string | undefined,
  userText: string,
  replyText: string,
  hasWording: boolean,
  backend: SuggestedAction[],
  normLabelOverride?: string,
): SuggestedAction[] {
  if (!lookupShowsAnschlussChips({
    chipAction,
    replyText,
    hasWording,
  })) {
    return backend;
  }
  const override = String(normLabelOverride || '').replace(/\s+/g, ' ').trim();
  return paragraphAnschlussChips(override || paragraphAnschlussNormLabel(userText));
}

/** Nur das Datum, das die Quelle schon mitliefert. */
export function quelleGueltigAbLine(validFrom?: string | null): string {
  if (typeof validFrom !== 'string') return '';
  const value = validFrom.replace(/\s+/g, ' ').trim();
  if (!value) return '';
  return `gültig ab ${value}`;
}
